import React from 'react';
import PropTypes from 'prop-types';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';

import { Banner } from 'core/components/banner';
import { Label } from 'core/components/label';

import useStyles from './policy-comments-banner.styles';

/**
 * Surfaces a migrated farm policy's source "COMMENTS OR RESTRICTIONS" comments as an informational
 * banner — distinct from the orange PolicyRestrictionsBanner. The source dec's comments are
 * informational migration notes (prior policy numbers, quote numbers, discount-program references,
 * workflow notes like "UPDATED LIABILITY TO BRANCH"), NOT coverage restrictions. #33009 deliberately
 * stopped migrating them into global.farmVerification.restrictions (which drives the alert banner and
 * the editable Details restriction box) because they fabricated a restriction on every policy — they
 * are archived on importedData.conversionMetadata.sourceComments instead.
 *
 * Comments and restrictions are separate concerns, so this banner renders independently of the
 * restriction field: a policy with only comments (and no restriction) still shows them. #33297
 *
 * It also surfaces the policy-type conversion note (importedData.conversionMetadata.policyTypeConversionNote)
 * when the migration assigned a Branch farm policyType that differs from the source type — i.e. a source
 * Home/Rental reclassified to Farm. That note explains WHY (e.g. ">40 acres", a farm liability code), so a
 * "why is this homeowner a Farm?" question is answerable from the record. It is non-null only when a
 * conversion happened. #35179
 */
const PolicyCommentsBanner = ({ policy }) => {
  const classes = useStyles();

  if (!isFarmBasedPolicyType(policy?.policyType)) {
    return null;
  }
  const comments = policy?.importedData?.conversionMetadata?.sourceComments?.trim();
  const conversionNote = policy?.importedData?.conversionMetadata?.policyTypeConversionNote?.trim();
  if (!comments && !conversionNote) {
    return null;
  }

  return (
    <Banner color="greenLight" show iconType="check">
      {conversionNote && (
        <Label type="smallWhite" className={classes.text}>
          <strong>Policy type:</strong> {conversionNote}
        </Label>
      )}
      {comments && (
        <Label type="smallWhite" className={classes.text}>
          <strong>Policy comments:</strong> {comments}
        </Label>
      )}
    </Banner>
  );
};

PolicyCommentsBanner.propTypes = {
  policy: PropTypes.shape({
    policyType: PropTypes.string,
    importedData: PropTypes.shape({
      conversionMetadata: PropTypes.shape({
        sourceComments: PropTypes.string,
        policyTypeConversionNote: PropTypes.string
      })
    })
  })
};

PolicyCommentsBanner.defaultProps = {
  policy: undefined
};

export default PolicyCommentsBanner;
